import React, { useCallback, useEffect, useRef } from 'react';

import {
  DROPDOWN_MAX_HEIGHT,
  DROPDOWN_MAX_HEIGHT_M2,
} from '@rover/constants/src/platform/AutocompleteMultiSelect.constants';
import { Box } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

export type AutocompleteDropdownOptionProps = {
  id: string;
  ariaSelected: boolean;
  onMouseDown: (e: React.SyntheticEvent) => void;
  onTouchStart: () => void;
  onTouchMove: () => void;
  onTouchEnd: (e: React.SyntheticEvent) => void;
  onMouseOver: () => void;
  onFocus?: () => void;
  isFocused: boolean;
  index?: number;
  maxHeightM2?: boolean;
  children: React.ReactNode;
};

const AutocompleteDropdownOption = (props: AutocompleteDropdownOptionProps): JSX.Element => {
  const { isFocused, index, maxHeightM2, ariaSelected, ...other } = props;
  const optionRef = useRef<HTMLDivElement>(null);
  const { textColor, colors, background } = useTheme();

  const assureOptionIsVisible = useCallback((): void => {
    if (!optionRef?.current?.getBoundingClientRect || !optionRef?.current?.parentElement) return;

    const { top: childTop, bottom: childBottom } = optionRef.current.getBoundingClientRect();
    const itemHeight = childBottom - childTop;

    if (index !== undefined && index >= 0) {
      const dropdownHeight = maxHeightM2 ? DROPDOWN_MAX_HEIGHT_M2 : DROPDOWN_MAX_HEIGHT;
      optionRef.current.parentElement.scroll?.({
        top: Math.max(
          0,
          itemHeight * (index === 0 ? 1 : index) - dropdownHeight / 2 + itemHeight / 2
        ),
      });
    }
  }, [index, optionRef, maxHeightM2]);

  useEffect(() => {
    if (isFocused) {
      assureOptionIsVisible();
    }
  }, [isFocused, assureOptionIsVisible]);

  return (
    <Box
      {...other}
      ref={optionRef}
      webProps={{ role: 'option' }}
      data-testid="input-dropdown-option"
      aria-selected={ariaSelected}
      sx={{
        cursor: 'pointer',
        padding: '5px 6px', // Added padding in sx because we don't have this values mapped in SpacingSystemType
        background: isFocused ? colors.cyan[600] : 'transparent',
        color: isFocused ? textColor.primary_inverse : textColor.primary,
        userSelect: 'none',
        '&:hover': {
          background: !isFocused ? background.highlight : colors.cyan['600'],
          color: !isFocused ? colors.text.primary : colors.text.primary_inverse,
        },
      }}
    >
      {props.children}
    </Box>
  );
};

export default AutocompleteDropdownOption;
